/**
 * filesystemApi — create-connector endpoint for the DESKTOP-ONLY filesystem
 * connector.
 *
 * BE source: ``ando-agent/src/api/v1/endpoints/filesystem.py`` —
 * ``POST /filesystem/connectors``. The connector row is created BEFORE the
 * native scan/upload (``fsSync``) runs so the uploaded files can be tagged
 * with the real ``connector_id``. ``apiClient`` prepends ``getEnv().apiUrl``
 * (already incl. the ``/api/v1`` version prefix) so the wire path resolves to
 * ``<apiUrl>/filesystem/connectors``.
 *
 * Contract (frozen to the FastAPI signature):
 *
 *   body:
 *     - ``name``            human-readable label (folder basename, desktop)
 *     - ``description?``    optional free-form note
 *     - ``base_paths``      absolute roots the agent is allowed to read
 *     - ``acl_mode``        ``"manual_mapping"`` for the desktop local-folder flow
 *     - ``deployment_mode?`` ``"on_premise"`` for the desktop Tauri shell
 *
 *   response:
 *     - ``id``              the freshly created connector id (mapped to
 *                            ``connector_id`` here so call sites stay vendor-
 *                            agnostic, matching ``importSpreadsheet``).
 */

import { apiClient } from "@ando/core/api";
import type { ConnectionSummary } from "@ando/core";

/** Wire response — the BE returns the new id under ``id`` (+ extra fields). */
interface CreateFilesystemConnectorResponse {
  readonly id: string;
  readonly [key: string]: unknown;
}

/**
 * Wire shape of a filesystem connector row (BE ``ConnectorResponse``).
 * The filesystem connector lives in its OWN table (``filesystem_connector_configs``),
 * NOT in the unified ``user_connections`` surface — so the connection list +
 * disconnect must hit ``/filesystem/connectors`` directly (the unified
 * ``/connectors/filesystem/connections`` route queries ``user_connections`` and
 * always returns ``[]`` → the "not connected" bug). This is the third
 * connection surface alongside white-label + enterprise.
 */
interface FilesystemConnectorRow {
  readonly id: string;
  readonly name: string;
  readonly description?: string | null;
  readonly base_paths: readonly string[];
  readonly selected_folders?: readonly string[];
  readonly acl_mode: string;
  readonly is_active: boolean;
  readonly last_sync_at?: string | null;
  readonly last_sync_status?: string | null;
  readonly total_files_indexed?: number;
  readonly created_at?: string | null;
}

/** Map a filesystem connector row to the shared ``ConnectionSummary`` shape so
 * the index grid + detail page treat it like any other connector (DRY with
 * ``mapEnterpriseToSummary``). No OAuth ⇒ no scope/token/reauth. */
function toSummary(row: FilesystemConnectorRow): ConnectionSummary {
  return {
    conn_id: row.id,
    vendor: "filesystem",
    capability: "filesystem",
    connection_key: row.name,
    scope: null,
    created_at: row.created_at ?? null,
    expires_at: null,
    last_used_at: row.last_sync_at ?? null,
    token_status: "active",
    needs_reauth: false,
    metadata: {
      acl_mode: row.acl_mode,
      is_active: row.is_active,
      last_sync_status: row.last_sync_status ?? null,
      total_files_indexed: row.total_files_indexed ?? 0,
      base_paths: row.base_paths,
      selected_folders: row.selected_folders ?? [],
    },
  };
}

export interface CreateFilesystemConnectorOptions {
  /** Human-readable label — desktop uses the picked folder's basename. */
  readonly name: string;
  /** Absolute roots the agent may scan. Desktop = ``[pickedFolderPath]``. */
  readonly basePaths: readonly string[];
  /** ACL strategy. Desktop local-folder defaults to ``"manual_mapping"``. */
  readonly aclMode?: string;
  /** Optional free-form note. */
  readonly description?: string;
}

/**
 * ``POST /filesystem/connectors`` — create the connector row.
 *
 * Returns ``{ connector_id }`` (response ``id`` re-keyed) so the dialog can
 * thread it into ``fsSync`` + ``syncStore.trackJob``.
 */
export async function createFilesystemConnector(
  opts: CreateFilesystemConnectorOptions,
): Promise<{ readonly connector_id: string }> {
  const res = await apiClient.post<CreateFilesystemConnectorResponse>(
    "/filesystem/connectors",
    {
      name: opts.name,
      base_paths: [...opts.basePaths],
      acl_mode: opts.aclMode ?? "manual_mapping",
      ...(opts.description !== undefined ? { description: opts.description } : {}),
    },
  );
  return { connector_id: String(res.id) };
}

/**
 * ``GET /filesystem/connectors`` → the connector list, mapped to
 * ``ConnectionSummary[]``. Called by ``listConnectionsForCapability`` for the
 * ``filesystem`` capability. Tolerates a bare array or a ``{connectors:[]}``
 * envelope; swallows errors to ``[]`` (parity with the other list adapters).
 */
export async function listFilesystemConnections(): Promise<readonly ConnectionSummary[]> {
  try {
    const raw = await apiClient.get<unknown>("/filesystem/connectors");
    const rows: readonly FilesystemConnectorRow[] = Array.isArray(raw)
      ? (raw as FilesystemConnectorRow[])
      : ((raw as { connectors?: readonly FilesystemConnectorRow[] })?.connectors ?? []);
    return rows.map(toSummary);
  } catch {
    return [];
  }
}

/** ``DELETE /filesystem/connectors/{id}`` — remove a filesystem connector. */
export async function deleteFilesystemConnection(connectorId: string): Promise<void> {
  await apiClient.delete<void>(`/filesystem/connectors/${encodeURIComponent(connectorId)}`);
}
